import { AfterViewInit, Component, Inject, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { some } from 'lodash';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  Device,
  EntityCacheProvider,
  TransformationService,
  DevicesService
} from '@nrwl/common-lib';

import { DeviceTableComponent } from './device-table/device-table.component';
import { SiteTableComponent } from './site-table/site-table.component';
import { MultipickerService } from '../service/multipicker.service';

@Component({
  selector: 'nrwl-multisite-picker',
  templateUrl: './multisite-picker.component.html',
  styleUrls: ['./multisite-picker.component.scss']
})
export class MultiSitePickerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(SiteTableComponent) siteComp: SiteTableComponent;
  @ViewChild(DeviceTableComponent) deviceComp: DeviceTableComponent;
  // eslint-disable-next-line
  siteList: any;
  isOpened = false;
  deviceTab: boolean;
  // eslint-disable-next-line
  devices: any;
  // eslint-disable-next-line
  sites: any;
  preSelectedDevices = [];
  preSelectedSites = [];
  initDeviceTab = false;
  // eslint-disable-next-line
  hidingSiteData: any;
  // eslint-disable-next-line
  hidingDeviceData: any;
  selectedTabIndex = 0;
  initSiteTab = false;
  siteTab: boolean;
  singleSelection = false;
  testType = null;
  private readonly unsubscribe: Subject<void> = new Subject();
  constructor(
    private entityCache: EntityCacheProvider,
    private transformService: TransformationService,
    private readonly deviceService: DevicesService,
    public dialogRef: MatDialogRef<MultiSitePickerComponent>,
    private readonly multipickerService: MultipickerService,
    @Inject(MAT_DIALOG_DATA) public data: any // eslint-disable-line
  ) {
    this.deviceTab = false;
  }

  ngOnInit(): void {
    this.testType = this.data?.testType || 'service';
    if (this.data && this.data.data && this.data.data.length > 0) {
      this.preSelectedDevices = this.data.data.filter(item => item.itemType === 'device');
      this.preSelectedSites = this.data.data.filter(item => item.itemType === 'site');
      if (
        (this.preSelectedDevices && this.preSelectedDevices.length > 0) ||
        (this.preSelectedSites && this.preSelectedSites.length > 0)
      ) {
        this.selectedTabIndex = this.preSelectedSites.length > 0 ? 0 : 1;
      }
    } else {
      this.multipickerService.selectedDevices = [];
      this.multipickerService.selectedSites = [];
    }
    if (this.data && this.data.hidingData) {
      if (this.data.hidingData.itemType === 'site') {
        this.hidingSiteData = this.data.hidingData;
      } else {
        this.hidingDeviceData = this.data.hidingData;
      }
    }
    this.singleSelection = this.data?.singleSelection;
    this.entityCache
      .findEntityCache('site')
      .getValuesAsync()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(site => {
        this.siteList = site;
        this.setContentData();
      });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.isOpened = true;
      this.initSiteTab = this.selectedTabIndex === 0;
      this.siteTab = this.initSiteTab;
      this.initDeviceTab = this.selectedTabIndex === 1;
      this.deviceTab = this.initDeviceTab;
    }, 300);
  }
  setContentData(): void {
    let siteList = this.siteList;
    this.entityCache
      .findEntityCache('device')
      .getValuesAsync()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(device => {
        let deviceList = device.filter(device => device.deviceData.type === 'GigaTestAgent');
        if (this.data.mergeData) {
          deviceList = [...deviceList, ...this.data.mergeData];
        }
        deviceList = this.filterAgents(deviceList);
        const destination = null;
        siteList = destination
          ? this.siteList.filter(site => site.sitesData.id !== destination)
          : this.siteList;
        const subContentList = [];
        for (const site of siteList) {
          const deviceListForSite = deviceList.filter(
            device => device.deviceData.site === site.sitesData.id
          );
          let online = false,
            offline = false;
          if (deviceListForSite.length) {
            online = some(deviceListForSite, device => device.deviceData.status === 'online');
            offline =
              !online && some(deviceListForSite, device => device.deviceData.status !== null);
            site.sitesData.devices = this.getUpdatedDeviceData(deviceListForSite);
            site.sitesData.closed = true;
            site.sitesData.hidden = false;
            site.sitesData.status = online ? 'online' : offline ? 'offline' : '';
            subContentList.push(site.sitesData);
          }
        }
        this.sites = subContentList;
        this.assignDeviceData();
      });
  }
  getUpdatedDeviceData(data: Device[]): Device[] {
    for (let i = 0; i < data.length; i++) {
      const platform = data[i].deviceData.params.find(x => x.name === 'platform')
        ? data[i].deviceData.params.find(x => x.name === 'platform').value
        : '';
      data[i].deviceData.iconName = this.transformService.getIconDetail(
        data[i].deviceData.status,
        platform
      );
      data[i].deviceData['hidden'] = false;
    }
    return data;
  }

  closeModal(): void {
    const selectedSites =
      this.siteComp && this.siteComp.siteTable && this.siteComp.siteTable.selection
        ? this.siteComp.siteTable.selection
        : [];
    const selectedDevices =
      this.deviceComp && this.deviceComp.deviceTable && this.deviceComp.deviceTable.selection
        ? this.deviceComp.deviceTable.selection
        : this.preSelectedDevices && this.preSelectedDevices.length > 0
        ? this.preSelectedDevices
        : [];
    this.dialogRef.close([...selectedSites, ...selectedDevices]);
  }

  assignDeviceData(): void {
    this.deviceService.cache.subscribe(data => {
      let agents = data;
      if (this.data.mergeData) {
        agents = [...agents, ...this.data.mergeData];
      }
      const filterData = this.filterAgents(agents);
      const multipickerDevices = this.transformService.multipickerDevices(filterData);
      this.devices = multipickerDevices;
    });
  }

  filterAgents(devices: Array<Device>): Array<Device> {
    return devices.filter(device => {
      const isWindowsAgent = device.deviceData.params.find(
        param => param.name === 'platform' && this.data?.hidingDeviceType?.includes(param.value)
      );
      return !isWindowsAgent;
    });
  }
  //eslint-disable-next-line
  onTabChange(tab: any): void {
    if (tab.index === 1) {
      this.initDeviceTab = true;
    } else {
      this.initSiteTab = true;
    }
    this.deviceTab = this.initDeviceTab;
    this.siteTab = this.initSiteTab;
  }

  disableSave(): boolean {
    const noData =
      (!this.deviceComp?.deviceTable?.selection ||
        this.deviceComp?.deviceTable?.selection?.length === 0) &&
      (!this.siteComp?.siteTable?.selection || this.siteComp?.siteTable?.selection?.length === 0);
    let noPreselectedDevices = true;
    if (!this.deviceComp) {
      noPreselectedDevices = this.preSelectedDevices.length === 0;
    }
    return noData && noPreselectedDevices;
  }

  ngOnDestroy(): void {
    this.unsubscribe.unsubscribe();
  }
}
